import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOrders from 'hooks/Order/useOrders'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { getEligibleEsimOrders } from 'loyalty/lib/getEligibleEsimOrders'
import fetchEsimPromoCodesAction from 'actions/loyalty/fetchEsimPromoCodes'
import { INITIAL_ESIM_PROMO_CODES_STATE } from 'reducers/loyaltyStateReducer'
import { useIntl } from 'react-intl'

export type EsimVariation = 'none' | 'single' | 'multiple'

export interface UseEsimPromoCodesResult {
  variation: EsimVariation
  codes: Array<App.LoyaltyEsimPromoCodeEntry>
  singleCode: App.LoyaltyEsimPromoCodeEntry | undefined
  singleOrderPhase: App.LoyaltyEsimCodePhase | undefined
  isLoading: boolean
  isEligibleListReady: boolean
}

interface UseEsimPromoCodesOptions {
  orderId?: string
}

function useEsimPromoCodes(options?: UseEsimPromoCodesOptions): UseEsimPromoCodesResult {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const orderIdFilter = options?.orderId
  const [orders] = useOrders('upcoming')
  const accountRegion = useAppSelector(getLoyaltyAccountRegion)
  const flightDetails = useAppSelector(state => state.orders.flightDetails)
  const rawEsimState = useAppSelector(state => state.loyalty.esimPromoCodes)
  const esimState = rawEsimState?.codesByOrderId ? rawEsimState : INITIAL_ESIM_PROMO_CODES_STATE
  const esimExcludedCountryCodes = useAppSelector(state => state.loyalty.config?.esimExcludedCountryCodes)
  const internationalTripLabel = intl.formatMessage({
    id: 'w2csFNE5b6Q3-38YywXP',
    defaultMessage: 'International trip',
    description: 'Fallback destination label for eligible eSIM orders when specific destination names are unavailable',
  })

  const hasOrdersWithCruise = useMemo(() => {
    const orderList = Array.isArray(orders) ? orders : []
    return orderList.some(
      o => o.status !== 'cancelled' && o.cruiseItems?.some(item => item.status === 'completed'),
    )
  }, [orders])

  const eligibleOrders = useMemo(() => {
    const orderList = Array.isArray(orders) ? orders : []
    if (!accountRegion || !orderList.length) return []
    const all = getEligibleEsimOrders(
      orderList,
      accountRegion,
      intl.locale,
      internationalTripLabel,
      flightDetails,
      esimState.cruiseCountryCodesByOrderId,
      esimExcludedCountryCodes,
    )
    if (orderIdFilter) return all.filter(o => o.orderId === orderIdFilter)
    return all
  }, [orders, accountRegion, orderIdFilter, flightDetails, esimState.cruiseCountryCodesByOrderId, esimExcludedCountryCodes, intl.locale, internationalTripLabel])

  const eligibleOrderIdsKey = useMemo(
    () => eligibleOrders.map(o => o.orderId).join(','),
    [eligibleOrders],
  )

  useEffect(() => {
    dispatch(fetchEsimPromoCodesAction(orderIdFilter))
  }, [dispatch, orderIdFilter, eligibleOrderIdsKey])

  const variation = useMemo<EsimVariation>(() => {
    if (eligibleOrders.length === 0) return 'none'
    if (eligibleOrders.length === 1) return 'single'
    return 'multiple'
  }, [eligibleOrders.length])

  const codes = useMemo(() => {
    const list = eligibleOrders
      .map(order => esimState.codesByOrderId[order.orderId])
      .filter((entry): entry is App.LoyaltyEsimPromoCodeEntry => !!entry && entry.phase !== 'not_found')
    // Show loading/generating first so the taller card doesn't create a hole in the grid; once done, order is by departure date
    const loadingOrGenerating = list.filter(c => c.phase === 'loading' || c.phase === 'generating')
    const rest = list.filter(c => c.phase !== 'loading' && c.phase !== 'generating')
    return [...loadingOrGenerating, ...rest]
  }, [eligibleOrders, esimState.codesByOrderId])

  const singleCode = variation === 'single' ? codes[0] : undefined
  const singleOrderPhase = useMemo(() => {
    if (orderIdFilter) return esimState.codesByOrderId[orderIdFilter]?.phase
    if (variation === 'single' && eligibleOrders.length === 1) return esimState.codesByOrderId[eligibleOrders[0].orderId]?.phase
    return undefined
  }, [orderIdFilter, variation, eligibleOrders, esimState.codesByOrderId])
  const isLoading = codes.some(code => code.phase === 'loading' || code.phase === 'generating')
  const isEligibleListReady = !hasOrdersWithCruise || esimState.cruiseCodesSettled

  return { variation, codes, singleCode, singleOrderPhase, isLoading, isEligibleListReady }
}

export default useEsimPromoCodes
